import { autobind } from "../helpers/autobind.mjs";
import { timeout } from "../utils/index.mjs";
import { sha256 } from "../utils/hash.mjs";
import { CACHE_TIMEOUT } from "../constants/index.mjs";
export const MINUTE = 60;
const CACHE_NOT_INITIALIZED_MSG = "Cache is not initialized";
export class Cached {
  cache;
  log;
  prefix;
  enabled;
  constructor(cache, log, prefix = "", enabled = process.env.AY_CACHE_DISABLED !== "true") {
    if (!cache) {
      log.error(CACHE_NOT_INITIALIZED_MSG);
      throw new Error(CACHE_NOT_INITIALIZED_MSG);
    }
    this.cache = cache;
    this.log = log.space("cached.init");
    this.prefix = prefix;
    this.enabled = enabled;
    autobind(this);
  }
  get isCacheEnabled() {
    return this.enabled;
  }
  execute(fn, options) {
    return async (...args) => {
      const prefix = options?.cacheKeyPrefix ?? fn.name;
      const params = [...args, this?.prefix];
      const cacheKey = options?.cacheKey ?? await this.createCacheKey(params, prefix);
      try {
        const cachedResponse = await this.getCacheValue(cacheKey, options);
        if (cachedResponse !== void 0 && cachedResponse !== null) {
          return cachedResponse;
        }
      } catch (e) {
        if (e instanceof Error) {
          this.handleError(e);
        }
      }
      const response = await fn(...args);
      if (response === void 0 || response === null) {
        return response;
      }
      if (response instanceof Response && !response.ok) {
        return response;
      }
      try {
        await this.setCacheValue(cacheKey, response, options);
      } catch (e) {
        if (e instanceof Error) {
          this.handleError(e);
        }
      }
      return response;
    };
  }
  async getCacheValue(cacheKey, options) {
    if (!this.isCacheEnabled) {
      return;
    }
    const data = await timeout(
      options?.timeout ?? CACHE_TIMEOUT,
      this.cache.get(cacheKey)
    );
    if (data !== null) {
      return data;
    }
  }
  async setCacheValue(cacheKey, value, options) {
    if (!this.isCacheEnabled) {
      return;
    }
    const ttl = options?.ttl ?? 60 * MINUTE;
    if (ttl) {
      await timeout(CACHE_TIMEOUT, this.cache.set(cacheKey, value, ttl));
    }
  }
  async createCacheKey(params, prefix = "") {
    return [prefix, await sha256(JSON.stringify(params))].join(":");
  }
  handleError(error) {
    if (error.message === "timeout") {
      return this.log.error("Cache timeout");
    }
    this.log.error(error);
  }
}
